import React, { useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService from '../../services/caregiverService';

/**
 * Confirmation modal for deactivating a caregiver-participant link (FECOA-1374).
 * Re-activation is intentionally not supported. The modal copy says so.
 *
 * Props:
 *   link: { link_id, caregiver: { id, ... }, relationship_type, ... } row from
 *         the Caregivers tab response. The caller passes either the row directly
 *         or any object with `link_id` + `caregiver` shape.
 *   caregiverId: optional override; falls back to link.caregiver.id.
 */
const DeactivateLinkConfirm = ({ link, caregiverId, onClose, onConfirmed }) => {
  const [submitting, setSubmitting] = useState(false);
  const cgId = caregiverId || link?.caregiver?.id;
  const linkId = link?.link_id || link?.id;
  const cgName =
    link?.caregiver?.full_name ||
    `${link?.caregiver?.first_name || ''} ${link?.caregiver?.last_name || ''}`.trim() ||
    link?.caregiver_id ||
    'this caregiver';

  const confirm = async () => {
    if (!cgId || !linkId) return;
    setSubmitting(true);
    try {
      await caregiverService.deactivateLink(cgId, linkId);
      toast.success('Caregiver link deactivated.');
      onConfirmed && onConfirmed();
    } catch (err) {
      console.error('deactivate failed', err);
      toast.error(err?.response?.data?.detail || 'Failed to deactivate link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Deactivate Caregiver Link</h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-start gap-3 p-3 mb-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 rounded-md">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800 dark:text-amber-200">
            <p className="font-semibold mb-1">This action cannot be reversed.</p>
            <p>
              Deactivating ends {cgName}&rsquo;s link to this participant for audit purposes.
              To reinstate this caregiver later, create a new link &mdash; the previous record stays
              archived for the audit trail.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-md text-sm text-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={submitting}
            className="px-4 py-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white rounded-md text-sm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Deactivate Link'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeactivateLinkConfirm;
